import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { withPage } from "../../hooks/CurrentPage";
import { getPreviousPath } from "../../hooks/PreviousPath";
import { useQueryUrl } from "../../hooks/QueryUrl";
import ChevronLeftIcon from "../../icons/ChevronLeft";
import { DESKTOP_BREAKPOINT } from "../AppShell/styles";
import { UnsavedChangesConfirmModal } from "../UnsavedChangesConfirmModal";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;
const DESKTOP = `(min-width: ${DESKTOP_BREAKPOINT})`;

const Container = styled.div<{ $hideOnMobile?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 1.25rem 2rem;

  /* The bottom line stops where the content does (the side padding), instead of running edge to edge */
  &::after {
    position: absolute;
    right: 2rem;
    bottom: 0;
    left: 2rem;
    height: 1px;
    content: "";
    background: var(--color-neutral-dark);
  }

  @media ${MOBILE} {
    padding: 20px;
    ${props => props.$hideOnMobile && "display: none;"}

    &::after {
      right: 20px;
      left: 20px;
    }
  }

  h1 {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    font-family: Poppins, sans-serif;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;

    @media ${MOBILE} {
      font-size: 22px;
      line-height: 30px;
    }
  }
`;

const Crumbs = styled.nav`
  display: flex;
  gap: 0.375rem;
  align-items: center;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  color: var(--color-neutral-150);

  button {
    overflow: hidden;
    color: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.15s;

    &:hover {
      color: var(--color-neutral-white);
    }
  }

  .back {
    display: flex;
    gap: 0.25rem;
    align-items: center;

    @media ${DESKTOP} {
      display: none;
    }

    /* ScreenHeader's single back link isn't a phone-only shortcut of a longer path */
    &.always {
      display: flex;
    }
  }

  &.mobile-only {
    @media ${DESKTOP} {
      display: none;
    }
  }
`;

/* The header's right element, vertically centered on the whole header (back link + title) */
const HeaderEnd = styled.div`
  position: absolute;
  top: 50%;
  right: 2rem;
  transform: translateY(-50%);

  @media ${MOBILE} {
    right: 20px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
`;

/* What the screen is for, under its title */
const Description = styled.p`
  margin: 0;
  color: var(--color-neutral-150);
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
`;

export type Crumb = {
  label: string;
  to: string;
};

type Props = {
  title: string;
  /** Levels between the Keepo and this screen, e.g. [{ label: "Seções", to: "/editor/sections" }]. */
  crumbs: Crumb[];
  /** When it returns true, following a crumb asks to discard the unsaved changes first. */
  hasUnsavedChanges?: boolean | (() => boolean);
  /** Shown at the end of the title row (e.g. the PLUS tag). */
  rightElement?: React.ReactNode;
  /** Replaces the default "discard and go" when leaving with unsaved changes (e.g. deleting a draft). */
  onConfirmExitUnsavedChanges?: () => void;
  /** Copy of the unsaved-changes modal, when the screen needs its own wording. */
  confirmExitModal?: { title?: string; confirmButton?: string; cancelButton?: string };
  /** Always navigates to the crumb, never back through history (when the previous screen isn't the crumb's). */
  noHistoryBack?: boolean;
};

type ScreenHeaderProps = {
  title: string;
  rightElement?: React.ReactNode;
  /** Hides the header on phones, where the bottom nav already identifies the screen. */
  hideOnMobile?: boolean;
  /** Phones only: a "‹ label" link above the title, for screens the bottom nav doesn't reach. */
  mobileBack?: Crumb;
  /** A "‹ label" link above the title on every screen size (flows outside a Keepo, e.g. checkout). */
  back?: Crumb;
  /** A line under the title saying what the screen is for, above the header's line. */
  description?: string;
};

/**
 * Title-only version of the compact header, for the panel's main screens (Conta, Ajuda,
 * Feedback, Meus Keepos, Meus domínios, Cartão de visitas): same look as the Keepo screens' header,
 * without breadcrumb or back button. The same size with or without a back link.
 */
export const ScreenHeader = ({
  title,
  rightElement,
  hideOnMobile,
  mobileBack,
  back,
  description,
}: ScreenHeaderProps) => {
  const navigate = useNavigate();

  // Back through history when we came from that screen, so the back gesture doesn't bounce
  const goBack = (to: string) => (getPreviousPath() === to ? navigate(-1) : navigate(to));
  const link = back || mobileBack;

  return (
    <Container $hideOnMobile={hideOnMobile}>
      {link && (
        <Crumbs aria-label="breadcrumb" className={back ? undefined : "mobile-only"}>
          <button type="button" className="back always" onClick={() => goBack(link.to)}>
            <ChevronLeftIcon width={10} height={10} color="currentColor" />
            {link.label}
          </button>
        </Crumbs>
      )}
      <TitleRow>
        <h1>{title}</h1>
        {/* With the back link above, it's centered on the whole header instead of the title's row */}
        {link ? <HeaderEnd>{rightElement}</HeaderEnd> : rightElement}
      </TitleRow>
      {description && <Description>{description}</Description>}
    </Container>
  );
};

/**
 * Compact header for screens inside a Keepo's editor (a section, an item): a breadcrumb
 * back to the Keepo's dashboard and the screen title, with no back button.
 */
export const SubHeader = ({
  title,
  crumbs,
  hasUnsavedChanges,
  rightElement,
  onConfirmExitUnsavedChanges,
  confirmExitModal,
  noHistoryBack,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // read from the URL (not useCurrentPage): the SubHeader also shows outside a Keepo
  const page = useQueryUrl().get("page");
  const [pendingPath, setPendingPath] = useState<string>();

  // A fixed label (not the Keepo's name) keeps the path short and predictable
  const path: Crumb[] = [{ label: t("components.subHeader.keepo"), to: "/keepo" }, ...crumbs];
  const previous = path[path.length - 1];

  const hasChanges = () => (typeof hasUnsavedChanges === "function" ? hasUnsavedChanges() : hasUnsavedChanges === true);

  // Back to the previous level: through history when that's the screen we came from (so the
  // browser/phone back gesture doesn't bounce between the two), otherwise opening it
  const follow = (to: string) => {
    const canGoBack = ((window.history.state?.idx as number | undefined) ?? 0) > 0;
    if (!noHistoryBack && canGoBack && getPreviousPath() === to.split("?")[0]) {
      navigate(-1);
    } else {
      navigate(to);
    }
  };

  const goTo = (path: string) => {
    // back to a screen of the editor: the same page of the Keepo (`?page`) that was being edited
    const to = path.startsWith("/editor/") ? withPage(path, page) : path;
    if (hasChanges()) {
      setPendingPath(to);
      return;
    }
    follow(to);
  };

  return (
    <>
      {pendingPath && (
        <UnsavedChangesConfirmModal
          onConfirmExit={() => {
            setPendingPath(undefined);
            if (onConfirmExitUnsavedChanges) {
              onConfirmExitUnsavedChanges();
            } else {
              follow(pendingPath);
            }
          }}
          onCloseModal={() => setPendingPath(undefined)}
          title={confirmExitModal?.title}
          confirmButtonTitle={confirmExitModal?.confirmButton}
          cancelTitle={confirmExitModal?.cancelButton}
        />
      )}
      <Container>
        {/* One way back on every screen size: "‹ previous level" above the title */}
        <Crumbs aria-label="breadcrumb">
          <button type="button" className="back always" onClick={() => goTo(previous.to)}>
            <ChevronLeftIcon width={10} height={10} color="currentColor" />
            {previous.label}
          </button>
        </Crumbs>
        <TitleRow>
          <h1>{title}</h1>
          {rightElement}
        </TitleRow>
      </Container>
    </>
  );
};
